/**
 * The first screen, and the one that matters.
 *
 * The job is to get someone who is half awake at six in the morning into a sit with as
 * few decisions as possible. So: the length they used last time is already selected,
 * the cadence they used last time is already selected, the recording is already drawn,
 * and there is one button, which starts the sit. Every other capability in the app is
 * reachable but downstream.
 *
 * The preview sits above that button and follows the choosing. It used to be a second
 * screen behind Begin, which meant the length was chosen blind and judged a page later,
 * and every sit cost two taps.
 *
 * It is also one viewport with no scrollbar, which is a real constraint rather than a
 * preference: on a phone the Begin button was below the fold, so the screen's whole
 * purpose needed a scroll to reach. Three bands — header, choosing, Sit — where the
 * middle one is the only flexible thing and everything in it is sized against `vh`. The
 * cadence hint is now one line under three chips rather than three two-line chips,
 * which is where most of the height came from.
 *
 * Durations that no recording can reach are shown disabled rather than hidden — the
 * corpus genuinely cannot do everything, and pretending otherwise means offering a
 * length and then failing to fill it.
 */

import { useMemo } from 'preact/hooks';
import type { Cadence, Recording, SessionPlan } from '../types';
import { formatDuration, settings, updateSettings } from '../state/settings';
import { manifest } from '../state/manifest';
import { InstallOffer } from './Install';
import { SitPreview } from './SitPreview';
import { TimeSpan, canBegin } from './TimeSpan';
import { formatClock, finishMs, intent, nowMs, targetSec } from '../state/timing';

const CADENCE: { id: Cadence; short: string; full: string; hint: string }[] = [
  {
    id: 'opening', short: 'Minimal', full: 'Opening and closing',
    hint: 'Guidance at the start and finish, with quiet between.',
  },
  {
    id: 'occasional', short: 'Occasional', full: 'Occasional reminders',
    hint: 'A word every ten minutes or so.',
  },
  {
    id: 'frequent', short: 'Frequent', full: 'Frequent guidance',
    hint: 'His voice returns often.',
  },
];

export interface HomePreview {
  recording: Recording | null;
  plan: SessionPlan | null;
  problem: string | null;
}

interface Props {
  /** Null while nothing can be drawn — no manifest yet, or no recording fits. */
  preview: HomePreview | null;
  /** The sit is loading. The button goes dead so a second tap cannot start a second sit. */
  starting: boolean;
  onSit: () => void;
  onSomethingElse: () => void;
  onLibrary: () => void;
  onSettings: () => void;
}

export function Home({
  preview, starting, onSit, onSomethingElse, onLibrary, onSettings,
}: Props) {
  const s = settings.value;
  const m = manifest.value;

  const it = intent.value;
  const now = nowMs.value;
  const canGo = useMemo(
    () => canBegin(it, s.cadence, now),
    // `now` deliberately participates: a fixed finish time can lapse while this screen
    // is open, and Sit must go dead when it does.
    [m, it, s.cadence, now],
  );

  const chosen = CADENCE.find((c) => c.id === s.cadence) ?? CADENCE[1];

  return (
    <div class="screen screen--fit">
      <div class="shell home-grid">
        {/* See .enso in styles.css. Decorative, so it is hidden from the reader, and
            wrapped because an oversized absolute child still grows the scroll area. */}
        <div class="enso-wrap" aria-hidden="true">
          <svg class="enso" viewBox="0 0 100 100" focusable="false">
            <circle cx="50" cy="50" r="46" pathLength="100"
                    stroke-dasharray="90 10" stroke-dashoffset="-5" />
          </svg>
        </div>

        <header class="spread home-head">
          <span class="eyebrow">Ajahn Brahm</span>
          <div class="row" style={{ gap: 'var(--gap-1)' }}>
            <button class="btn btn-quiet" onClick={onLibrary}>Library</button>
            <button class="btn btn-quiet" onClick={onSettings}>Settings</button>
          </div>
        </header>

        <div class="home-main">
          <TimeSpan cadence={s.cadence} />

          <fieldset class="stack-sm" style={{ border: 0, padding: 0, margin: 0,
                                              gap: 'var(--v-tight)' }}>
            <legend class="visually-hidden">How often he speaks</legend>
            <div class="chip-row cadence" style={{ '--cols': CADENCE.length }}>
              {CADENCE.map((c) => (
                <button
                  key={c.id}
                  class="chip"
                  aria-pressed={s.cadence === c.id}
                  aria-label={c.full}
                  title={c.hint}
                  onClick={() => updateSettings({ cadence: c.id })}
                >
                  {c.short}
                </button>
              ))}
            </div>
            {/* One hint, for the chip that is actually selected. Three chips each
                carrying their own hint cost two lines apiece and pushed Begin off a
                390 px screen. */}
            <p class="cadence-hint" aria-live="polite">{chosen.hint}</p>
          </fieldset>

          {canGo && preview && (
            <SitPreview
              recording={preview.recording}
              plan={preview.plan}
              problem={preview.problem}
              onSomethingElse={onSomethingElse}
            />
          )}
        </div>

        <div class="home-foot">
          <InstallOffer />
          <button class="btn btn-primary home-begin"
                  disabled={!canGo || !preview?.plan || starting}
                  onClick={onSit}>
            {starting
              ? 'Starting…'
              : it.kind === 'deadline'
                ? `Sit — finish at ${formatClock(finishMs(it, now))}`
                : `Sit — ${formatDuration(targetSec(it, now))}`}
          </button>
        </div>
      </div>
    </div>
  );
}
